import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Clock, Trash2, Edit3, Plus, Loader2, Users, GraduationCap, MapPin, Trophy, Sparkles, Layers, Search, X, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";

const TARGET_TYPES = [
  { value: "class",       label: "Class(es)",                icon: GraduationCap },
  { value: "contact",     label: "One Person",               icon: Users },
  { value: "location",    label: "Location",                 icon: MapPin },
  { value: "division",    label: "Division (Elite/Company)", icon: Trophy },
  { value: "program",     label: "Program (Rec/Comp)",       icon: Sparkles },
  { value: "everyone",    label: "Everyone",                 icon: Layers },
];

const DIVISIONS = [
  { value: "Elite",   label: "Elite (Competitive)" },
  { value: "Company", label: "Company (Competitive)" },
];

const PROGRAMS = [
  { value: "Recreational", label: "Recreational (all classes)" },
  { value: "Competitive",  label: "Competitive (all Elite + Company)" },
];

export default function Scheduled() {
  const [items, setItems] = useState([]);
  const [sortDir, setSortDir] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [contacts, setContacts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [targetType, setTargetType] = useState("class");
  const [targetValue, setTargetValue] = useState("");     // for single-target types
  const [classIds, setClassIds] = useState([]);           // for class multi-select
  const [classSearch, setClassSearch] = useState("");
  const [locationCodes, setLocationCodes] = useState([]); // for location multi-select
  const [locProgram, setLocProgram] = useState("All");    // Recreational / Competitive filter for locations
  const [body, setBody] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: sched }, { data: cts }, { data: cls }, { data: locs }] = await Promise.all([
        api.get("/scheduled"), api.get("/contacts"), api.get("/classes"), api.get("/locations"),
      ]);
      setItems(sched);
      setContacts(cts);
      setClasses(cls);
      setLocations(locs || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null); setTargetType("class"); setTargetValue("");
    setClassIds([]); setClassSearch("");
    setLocationCodes([]); setLocProgram("All");
    setBody(""); setDate(null); setTime("09:00");
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    let t = "class", v = "", cids = [], lcodes = [];
    if (Array.isArray(it.class_ids) && it.class_ids.length) { t = "class"; cids = it.class_ids; }
    else if (it.class_id) { t = "class"; cids = [it.class_id]; }
    else if (it.contact_id) { t = "contact"; v = it.contact_id; }
    else if (Array.isArray(it.location_codes) && it.location_codes.length) { t = "location"; lcodes = it.location_codes; }
    else if (it.location_code) { t = "location"; lcodes = [it.location_code]; }
    else if (it.division) { t = "division"; v = it.division; }
    else if (it.program) { t = "program"; v = it.program; }
    else if (it.everyone) { t = "everyone"; v = "everyone"; }
    setTargetType(t);
    setTargetValue(v);
    setClassIds(cids);
    setLocationCodes(lcodes);
    setLocProgram(t === "location" && it.program ? it.program : "All");
    setClassSearch("");
    setBody(it.body);
    const dt = new Date(it.send_at);
    setDate(dt);
    setTime(`${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`);
    setOpen(true);
  };

  const save = async () => {
    if (!body.trim()) return toast.error("Type a message");
    if (!date) return toast.error("Pick a date");
    if (targetType === "class" && classIds.length === 0) return toast.error("Add at least one class");
    if (targetType === "location" && locationCodes.length === 0) return toast.error("Add at least one location");
    if (targetType !== "everyone" && targetType !== "class" && targetType !== "location" && !targetValue) return toast.error("Pick a target");
    const [hh, mm] = time.split(":");
    const dt = new Date(date); dt.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
    const payload = {
      body,
      send_at: dt.toISOString(),
      class_id:        null,
      class_ids:       targetType === "class"    ? classIds : null,
      contact_id:      targetType === "contact"  ? targetValue : null,
      location_code:   null,
      location_codes:  targetType === "location" ? locationCodes : null,
      division:        targetType === "division" ? targetValue : null,
      program:         targetType === "program"  ? targetValue : (targetType === "location" && locProgram !== "All" ? locProgram : null),
      everyone:        targetType === "everyone",
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/scheduled/${editing.id}`, payload);
      else await api.post("/scheduled", payload);
      toast.success(editing ? "Updated" : "Scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Cancel this scheduled message?")) return;
    try { await api.delete(`/scheduled/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const classById = useMemo(() => Object.fromEntries(classes.map(c => [c.id, c])), [classes]);
  const contactById = useMemo(() => Object.fromEntries(contacts.map(c => [c.id, c])), [contacts]);
  const locationByCode = useMemo(() => Object.fromEntries(locations.map(l => [l.code, l])), [locations]);

  const labelFor = (it) => {
    const cids = Array.isArray(it.class_ids) && it.class_ids.length ? it.class_ids : (it.class_id ? [it.class_id] : []);
    if (cids.length) {
      if (cids.length === 1) {
        const c = classById[cids[0]];
        return { icon: GraduationCap, text: c ? `Class · ${c.name}` : "Class" };
      }
      const names = cids.map(id => classById[id]?.name).filter(Boolean);
      return { icon: GraduationCap, text: `${cids.length} classes · ${names.slice(0,2).join(", ")}${names.length>2?`, +${names.length-2} more`:""}` };
    }
    if (it.contact_id) {
      const c = contactById[it.contact_id];
      return { icon: Users, text: c ? `${c.first_name} ${c.last_name}` : "Contact" };
    }
    if (it.location_code || (Array.isArray(it.location_codes) && it.location_codes.length)) {
      const codes = Array.isArray(it.location_codes) && it.location_codes.length ? it.location_codes : [it.location_code];
      const prog = it.program ? ` · ${it.program} only` : "";
      if (codes.length === 1) {
        const l = locationByCode[codes[0]];
        return { icon: MapPin, text: `Location · ${l ? l.label : codes[0]}${prog}` };
      }
      const labels = codes.map(code => locationByCode[code]?.label || code).filter(Boolean);
      return { icon: MapPin, text: `${codes.length} locations · ${labels.slice(0,2).join(", ")}${labels.length>2?`, +${labels.length-2} more`:""}${prog}` };
    }
    if (it.division) return { icon: Trophy,    text: `Division · ${it.division}` };
    if (it.program)  return { icon: Sparkles,  text: `Program · ${it.program}` };
    if (it.everyone) return { icon: Layers,    text: "Everyone" };
    return { icon: Clock, text: "—" };
  };

  const secondSelectOptions = useMemo(() => {
    switch (targetType) {
      case "contact":  return contacts.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name} (${c.phone})` }));
      case "division": return DIVISIONS;
      case "program":  return PROGRAMS;
      default:         return [];
    }
  }, [targetType, contacts]);

  const filteredClasses = useMemo(() => {
    const s = classSearch.trim().toLowerCase();
    if (!s) return classes;
    return classes.filter(c => (c.name || "").toLowerCase().includes(s));
  }, [classes, classSearch]);

  const toggleClass = (id) => {
    setClassIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const addAllVisibleClasses = () => {
    setClassIds(prev => Array.from(new Set([...prev, ...filteredClasses.map(c => c.id)])));
  };
  const toggleLocation = (code) => {
    setLocationCodes(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  };

  const shown = items
    .filter((i) => statusFilter === "all" || i.status === statusFilter)
    .sort((a, b) => (sortDir === "asc" ? 1 : -1) * String(a.send_at || "").localeCompare(String(b.send_at || "")));

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="scheduled-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Scheduled Messages</h1>
            <p className="text-sm text-slate-500 mt-1">{items.filter(i => i.status === "pending").length} pending &middot; edit or cancel any time</p>
          </div>
          <Button onClick={openNew} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="new-scheduled-button">
            <Plus className="w-4 h-4 mr-2" /> Schedule Message
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="scheduled-filters">
          {["all", "pending", "sent", "failed", "cancelled"].map((s) => (
            <button key={s} type="button" onClick={() => setStatusFilter(s)} data-testid={`scheduled-status-${s}`}
              className={`h-8 px-3 rounded-md text-xs font-semibold capitalize transition-colors ${statusFilter === s ? "bg-[#D9232E] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {s} ({s === "all" ? items.length : items.filter((i) => i.status === s).length})
            </button>
          ))}
          <button type="button" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))} data-testid="scheduled-sort-toggle"
            className="ml-auto h-8 px-3 rounded-md text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
            {sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
            {sortDir === "asc" ? "Soonest first" : "Latest first"}
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="scheduled-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Send At</th>
                <th className="text-left px-4 py-3 font-semibold">Recipient</th>
                <th className="text-left px-4 py-3 font-semibold">Message</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400">{items.length ? "No messages match this filter" : "No scheduled messages"}</td></tr>}
              {shown.map((it) => {
                const lbl = labelFor(it);
                const Ic = lbl.icon;
                return (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`scheduled-row-${it.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" />{new Date(it.send_at).toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-1.5"><Ic className="w-3.5 h-3.5 text-slate-400" />{lbl.text}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-md truncate">{it.body}</td>
                    <td className="px-4 py-3">
                      <Badge variant={it.status === "pending" ? "secondary" : it.status === "sent" ? "default" : "destructive"}>{it.status}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {it.status === "pending" && (
                          <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-scheduled-${it.id}`}>
                            <Edit3 className="w-4 h-4 text-slate-500" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => del(it.id)} data-testid={`delete-scheduled-${it.id}`}>
                          <Trash2 className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="scheduled-dialog">
            <DialogHeader><DialogTitle>{editing ? "Edit Scheduled Message" : "Schedule New Message"}</DialogTitle></DialogHeader>
            <div className="space-y-4 min-w-0">
              <div>
                <Label>Send to</Label>
                <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTargetValue(v === "everyone" ? "everyone" : ""); setClassIds([]); setLocationCodes([]); setClassSearch(""); }}>
                  <SelectTrigger className="mt-1" data-testid="target-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TARGET_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`target-type-${t.value}`}>
                        <span className="inline-flex items-center gap-2"><t.icon className="w-3.5 h-3.5" />{t.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {targetType === "class" && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Classes <span className="text-[#D9232E]">({classIds.length})</span></Label>
                    {classIds.length > 0 && (
                      <button type="button" onClick={() => setClassIds([])} className="text-xs text-slate-500 hover:text-[#D9232E]" data-testid="clear-classes">
                        Clear all
                      </button>
                    )}
                  </div>
                  {/* Selected class chips */}
                  {classIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                      {classIds.map(id => {
                        const c = classById[id];
                        return (
                          <Badge key={id} variant="secondary" className="pl-2 pr-1 py-1 bg-white border border-slate-200 text-slate-700 gap-1" data-testid={`selected-class-${id}`}>
                            <span className="text-xs">{c ? c.name : id}</span>
                            <button type="button" onClick={() => toggleClass(id)} className="ml-0.5 hover:text-[#D9232E]" data-testid={`remove-class-${id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  {/* Search + list */}
                  <div className="relative mt-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search classes..."
                      value={classSearch}
                      onChange={(e) => setClassSearch(e.target.value)}
                      className="pl-9 h-9 bg-slate-50 border-slate-200"
                      data-testid="class-search-input"
                    />
                  </div>
                  {classSearch && filteredClasses.length > 0 && (
                    <div className="mt-1 mb-1 flex justify-end">
                      <button type="button" onClick={addAllVisibleClasses} className="text-xs text-[#D9232E] hover:underline" data-testid="add-all-visible-classes">
                        + Add all {filteredClasses.length} matching
                      </button>
                    </div>
                  )}
                  <div className="h-52 mt-1 border border-slate-200 rounded-md bg-white overflow-y-auto overscroll-contain thin-scroll" data-testid="scheduled-class-list">
                    {filteredClasses.length === 0 ? (
                      <div className="p-6 text-center text-xs text-slate-400">No classes match.</div>
                    ) : filteredClasses.map((c) => {
                      const isPicked = classIds.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleClass(c.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 border-b border-slate-100 text-left transition-colors ${isPicked ? "bg-red-50" : "hover:bg-slate-50"}`}
                          data-testid={`toggle-class-${c.id}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isPicked ? "bg-[#D9232E] border-[#D9232E]" : "border-slate-300"}`}>
                            {isPicked && <span className="text-white text-[10px] leading-none">✓</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{c.location_code || "Victoria"} · {c.division || c.program || ""}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {targetType === "location" && (
                <div>
                  <Label>Who at these locations</Label>
                  <div className="flex gap-1.5 mt-1.5 mb-3" data-testid="location-program-filter">
                    {[["All", "Everyone"], ["Recreational", "Recreational only"], ["Competitive", "Competitive only"]].map(([k, label]) => (
                      <button key={k} type="button" onClick={() => setLocProgram(k)} data-testid={`location-program-${k.toLowerCase()}`}
                        className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${locProgram === k ? "bg-[#D9232E] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Locations <span className="text-[#D9232E]">({locationCodes.length})</span></Label>
                    {locationCodes.length > 0 && (
                      <button type="button" onClick={() => setLocationCodes([])} className="text-xs text-slate-500 hover:text-[#D9232E]" data-testid="clear-locations">
                        Clear all
                      </button>
                    )}
                  </div>
                  {locationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                      {locationCodes.map(code => {
                        const l = locationByCode[code];
                        return (
                          <Badge key={code} variant="secondary" className="pl-2 pr-1 py-1 bg-white border border-slate-200 text-slate-700 gap-1" data-testid={`selected-location-${code}`}>
                            <span className="text-xs">{l ? l.label : code}</span>
                            <button type="button" onClick={() => toggleLocation(code)} className="ml-0.5 hover:text-[#D9232E]" data-testid={`remove-location-${code}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <div className="h-48 mt-1 border border-slate-200 rounded-md bg-white overflow-y-auto overscroll-contain thin-scroll" data-testid="scheduled-location-list">
                    {locations.length === 0 ? (
                      <div className="p-6 text-center text-xs text-slate-400">No locations found.</div>
                    ) : locations.map((l) => {
                      const isPicked = locationCodes.includes(l.code);
                      return (
                        <button
                          key={l.code}
                          type="button"
                          onClick={() => toggleLocation(l.code)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 border-b border-slate-100 text-left transition-colors ${isPicked ? "bg-red-50" : "hover:bg-slate-50"}`}
                          data-testid={`toggle-location-${l.code}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isPicked ? "bg-[#D9232E] border-[#D9232E]" : "border-slate-300"}`}>
                            {isPicked && <span className="text-white text-[10px] leading-none">✓</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{l.label}</div>
                            <div className="text-[11px] text-slate-500 truncate" data-testid={`location-family-count-${l.code}`}>{(locProgram === "All" ? l.student_count : (l.program_counts || {})[locProgram]) || 0} families{locProgram !== "All" ? ` (${locProgram.toLowerCase()})` : ""}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {targetType !== "everyone" && targetType !== "class" && targetType !== "location" && (
                <div>
                  <Label>{
                    targetType === "contact"  ? "Choose contact" :
                    targetType === "location" ? "Choose location" :
                    targetType === "division" ? "Choose division" :
                                                "Choose program"
                  }</Label>
                  <Select value={targetValue} onValueChange={setTargetValue}>
                    <SelectTrigger className="mt-1" data-testid="target-value-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="max-h-[280px]">
                      {secondSelectOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} data-testid={`target-value-${opt.value}`}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {targetType === "everyone" && (
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Will send an individual text to every contact on file (STOPped contacts are skipped automatically).
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1" data-testid="scheduled-date-button">
                        {date ? new Date(date).toLocaleDateString() : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0"><Calendar mode="single" selected={date} onSelect={setDate} /></PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" data-testid="scheduled-time-input" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Message</Label>
                  <TemplatePicker onSelect={(b) => setBody(b)} testId="scheduled-template-picker" />
                </div>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="scheduled-body" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-scheduled-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Update" : "Schedule")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
